import React, { useState } from 'react';
import { useTaxCaseDocuments } from '../hooks/useTaxCaseDocuments';
import { updateDocumentStatus, getDocumentDownloadUrl } from '../services/firestoreService';
import SignaturePortal from './SignaturePortal';
import { TaxDocument } from '../types';
import { useAuth } from '../context/AuthContext';

interface CaseDocumentsPanelProps {
  caseId: string;
  isStaff: boolean;
}

const CaseDocumentsPanel: React.FC<CaseDocumentsPanelProps> = ({ caseId, isStaff }) => {
  const { documents, loading, error } = useTaxCaseDocuments(caseId);
  const { firmId } = useAuth();
  const [fetchingId, setFetchingId] = useState<string | null>(null);
  const [activeSignDoc, setActiveSignDoc] = useState<TaxDocument | null>(null);

  const handleStatusChange = async (docId: string, newStatus: string) => {
    // Fix: pass firmId to updateDocumentStatus
    if (!firmId) return;
    try {
      // Fix: pass firmId as first argument
      await updateDocumentStatus(firmId, caseId, docId, newStatus);
    } catch (e) {
      console.error("Failed to update status", e);
      alert("Error updating document status.");
    }
  };

  const handleDownload = async (docId: string, storagePath: string) => {
    setFetchingId(docId);
    try {
      const url = await getDocumentDownloadUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error("Download failed", e);
      alert("Could not retrieve secure download link. Please try again later.");
    } finally {
      setFetchingId(null);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📕';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('officedocument')) return '📘';
    return '📄';
  };

  if (loading) return <div className="py-12 text-center text-slate-500 animate-pulse">Loading secure documents...</div>;
  if (error) return <div className="py-8 text-center text-red-500 bg-red-50 rounded-xl m-4 border border-red-100">{error}</div>;

  return (
    <>
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <span className="text-brand-600">📂</span> Tax Return Documents
        </h3>
        <span className="text-xs bg-brand-100 text-brand-700 font-bold px-2 py-1 rounded-full">{documents.length} Files</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-6 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">File Info</th>
              <th className="px-6 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Category</th>
              <th className="px-6 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Date</th>
              <th className="px-6 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Status</th>
              <th className="px-6 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {documents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                   <div className="text-slate-300 text-3xl mb-2">📭</div>
                   <p className="text-slate-400">No documents found in this folder.</p>
                </td>
              </tr>
            ) : (
              documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl group-hover:scale-110 transition-transform">
                        {getFileIcon(doc.mimeType)}
                      </span>
                      <div className="overflow-hidden">
                        <p className="font-semibold text-slate-800 truncate max-w-[180px]" title={doc.fileName}>
                          {doc.fileName}
                        </p>
                        <p className="text-[10px] text-slate-400 font-medium">
                          {(doc.sizeBytes / 1024 / 1024).toFixed(2)} MB • {doc.mimeType.split('/')[1].toUpperCase()}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-block px-2 py-0.5 text-[10px] font-bold bg-slate-100 text-slate-600 rounded uppercase tracking-wide">
                      {doc.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-500 tabular-nums">
                    {new Date(doc.uploadedAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={doc.status} isStaff={isStaff} />
                      {isStaff && (
                        <select
                          value={doc.status}
                          onChange={(e) => handleStatusChange(doc.id, e.target.value)}
                          className="mt-1 px-1.5 py-0.5 text-[10px] border border-slate-200 rounded bg-white text-slate-600 focus:ring-1 focus:ring-brand-500 outline-none"
                        >
                          <option value="PENDING">Pending</option>
                          <option value="APPROVED">Approved</option>
                          <option value="REJECTED">Rejected</option>
                          <option value="ADDED_TO_RETURN">Processed</option>
                          <option value="ISSUE">Signature Required</option>
                        </select>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleDownload(doc.id, doc.storagePath)}
                        disabled={fetchingId === doc.id}
                        className="flex items-center gap-2 text-brand-600 hover:text-brand-800 font-bold transition-all disabled:opacity-50 text-xs"
                      >
                        {fetchingId === doc.id ? (
                          <span className="w-3 h-3 border-2 border-brand-600 border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                        <span>View Securely</span>
                      </button>
                      
                      {isStaff && doc.status !== 'ADDED_TO_RETURN' && (
                         <button
                           onClick={() => setActiveSignDoc(doc)}
                           className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-bold transition-all text-xs"
                         >
                           <span>✍️</span>
                           <span>Prepare Signatures</span>
                         </button>
                      )}

                      {!isStaff && doc.status === 'ISSUE' && (
                        <button
                          onClick={() => setActiveSignDoc(doc)}
                          className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-bold transition-all text-xs animate-pulse"
                        >
                          <span>✍️</span>
                          <span>Review & Sign</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>

    {activeSignDoc && (
        <SignaturePortal 
            caseId={caseId}
            document={activeSignDoc}
            isStaff={isStaff}
            onClose={() => setActiveSignDoc(null)}
        />
    )}
    </>
  );
};

const StatusBadge = ({ status, isStaff }: { status: string, isStaff: boolean }) => {
  let displayStatus = status;
  if (!isStaff && status === 'ADDED_TO_RETURN') displayStatus = 'APPROVED';

  let styles = "bg-slate-100 text-slate-600";
  if (displayStatus === 'APPROVED' || displayStatus === 'ADDED_TO_RETURN') styles = "bg-emerald-50 text-emerald-700 border border-emerald-100";
  if (displayStatus === 'REJECTED') styles = "bg-red-50 text-red-700 border border-red-100";
  if (displayStatus === 'PENDING') styles = "bg-amber-50 text-amber-700 border border-amber-200";
  if (displayStatus === 'ISSUE') styles = "bg-indigo-50 text-indigo-700 border border-indigo-100";
  
  return (
    <span className={`px-2 py-0.5 text-[9px] font-black rounded uppercase tracking-widest ${styles}`}>
      {displayStatus === 'ISSUE' ? 'Needs Signature' : displayStatus.replace(/_/g, ' ')}
    </span>
  );
};

export default CaseDocumentsPanel;
